/**
 * BillingSuccess — Stripe Checkout redirect target after a successful (or
 * cancelled) payment.
 *
 * Stripe sends users back to `/billing/success?session_id={CHECKOUT_SESSION_ID}`.
 * We poll `/api/billing/status/{session_id}` every 2s up to 5 attempts.
 * The backend grants Pro idempotently — re-polling is safe.
 *
 * The /billing/cancel route uses this same component but bails to the studio
 * after a soft confirmation, since Stripe's "cancel" simply sends the user
 * back without any session id.
 */

import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import ElpmLogo from "@/components/ElpmLogo";
import { CheckCircle2, AlertCircle, Loader2, Home, XCircle, Crown } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_ATTEMPTS = 8;       // ~16 seconds end-to-end
const POLL_INTERVAL = 2000;

export default function BillingSuccess() {
  const location = useLocation();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const isCancel = location.pathname.startsWith("/billing/cancel");
  const [status, setStatus] = useState(isCancel ? "cancelled" : "polling");
  const [attempt, setAttempt] = useState(0);
  const guardRef = useRef(false);

  useEffect(() => {
    if (isCancel) return undefined;
    if (!sessionId) {
      setStatus("missing-session");
      return undefined;
    }
    if (guardRef.current) return undefined;
    guardRef.current = true;

    let cancelled = false;
    let timerId = null;

    async function pollOnce(n) {
      if (cancelled) return;
      setAttempt(n);
      try {
        const res = await fetch(`${API}/api/billing/status/${encodeURIComponent(sessionId)}`, {
          credentials: "include",
        });
        // Read the body ONCE — eliminates every flavour of "body stream already
        // read" / "Response body is already used" by never reaching for the
        // body again. Parse-or-throw based on res.ok.
        const text = await res.text();
        if (!res.ok) throw new Error(text || `HTTP ${res.status}`);
        let body;
        try { body = JSON.parse(text); } catch { throw new Error(`Bad JSON: ${text.slice(0, 80)}`); }
        if (body.status === "completed" && (body.granted || body.payment_status === "paid")) {
          setStatus("done");
          toast.success("Pro activated", {
            description: "Your 10× quotas are live across song generation · stems · voice.",
          });
          return;
        }
        if (body.status === "expired") {
          setStatus("expired");
          return;
        }
        if (body.status === "failed") {
          setStatus("failed");
          return;
        }
        // Still pending → schedule next poll.
        if (n < MAX_ATTEMPTS) {
          timerId = window.setTimeout(() => pollOnce(n + 1), POLL_INTERVAL);
        } else {
          setStatus("timeout");
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[billing] status poll failed:", e);
        if (n < MAX_ATTEMPTS) {
          timerId = window.setTimeout(() => pollOnce(n + 1), POLL_INTERVAL);
        } else {
          setStatus("error");
        }
      }
    }

    pollOnce(1);
    return () => {
      cancelled = true;
      if (timerId) window.clearTimeout(timerId);
    };
  }, [sessionId, isCancel]);

  return (
    <div
      data-testid="billing-result"
      className="flex min-h-screen items-center justify-center bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 p-6 text-white"
    >
      <div className="w-full max-w-md rounded-3xl border border-white/[0.1] bg-zinc-900/85 p-8 shadow-[0_24px_64px_-16px_rgba(0,0,0,0.65)]">
        <ElpmLogo className="mx-auto mb-6 h-12" testId="billing-logo" />
        {status === "polling" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-fuchsia-300" />
            <h1 data-testid="billing-status-label" className="mt-5 text-center font-display text-[22px] font-semibold text-white">
              Activating your Pro pass…
            </h1>
            <p className="mt-2 text-center font-body text-[13px] text-white/60">
              Confirming payment with Stripe · attempt {attempt} of {MAX_ATTEMPTS}
            </p>
          </>
        )}

        {status === "done" && (
          <>
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-500/20 ring-1 ring-emerald-400/40">
              <CheckCircle2 className="h-9 w-9 text-emerald-300" />
            </div>
            <h1 data-testid="billing-status-label" className="mt-5 text-center font-display text-[22px] font-semibold text-white">
              Welcome to Pro
              <Crown className="ml-1 inline h-5 w-5 text-amber-300" />
            </h1>
            <p className="mt-2 text-center font-body text-[13px] text-white/75">
              Your 10× quotas are active across song generation, stem extraction, lead generation and voice.
            </p>
          </>
        )}

        {(status === "cancelled" || status === "expired" || status === "failed" || status === "timeout" || status === "error" || status === "missing-session") && (
          <>
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-amber-500/20 ring-1 ring-amber-400/40">
              {status === "cancelled" ? <XCircle className="h-9 w-9 text-amber-300" /> : <AlertCircle className="h-9 w-9 text-amber-300" />}
            </div>
            <h1 data-testid="billing-status-label" className="mt-5 text-center font-display text-[22px] font-semibold text-white">
              {status === "cancelled" ? "Checkout cancelled" : "We couldn't confirm your payment"}
            </h1>
            <p className="mt-2 text-center font-body text-[13px] text-white/65">
              {status === "cancelled" && "Your card was not charged. Pick up where you left off in the studio."}
              {status === "expired" && "The checkout session expired. Try again from the Pricing menu."}
              {status === "failed" && "Stripe reported the payment as failed. Try again or use a different card."}
              {status === "timeout" && "Stripe is taking longer than usual to confirm. Your account will update automatically once the webhook fires — feel free to head back to the studio."}
              {status === "error" && "We had trouble talking to Stripe. The webhook may still confirm in the background — your account will update automatically."}
              {status === "missing-session" && "No checkout session found on the URL. Heading back to the studio."}
            </p>
          </>
        )}

        <Link
          to="/"
          data-testid="billing-home-link"
          className="mt-8 flex items-center justify-center gap-2 rounded-2xl border border-white/[0.12] bg-white/[0.04] py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-white/85 transition hover:bg-white/[0.1]"
        >
          <Home className="h-3.5 w-3.5" />
          Back to the studio
        </Link>
      </div>
    </div>
  );
}
